import { FC, useEffect, useCallback, createContext, useContext } from 'react'
import { getUseBotOnboardingStore } from '~/features/bots/stores/useBotOnboardingStore'
import { showStickyToast, useSidebar } from '@bpinternal/ui-kit-next'
import { BotDeployToast } from '~/features/bots/components/onboarding/BotDeployToast'
import { useAppStore } from '~/stores'
import { useQuery } from '@tanstack/react-query'
import { listBotsQueryOptions } from '~/queries/bots'
import { useLocation } from 'react-use'

type DeploymentToastContextType = {
  showDeployToast: (workspaceId: string, botId: string) => void
}

const DeploymentToastContext = createContext<DeploymentToastContextType | null>(null)

export const useDeploymentToast = () => {
  const context = useContext(DeploymentToastContext)
  if (!context) {
    throw new Error('useDeploymentToast must be used within DeploymentToastProvider')
  }
  return context
}

export const DeploymentToastProvider: FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isMobile } = useSidebar()
  const activeWorkspace = useAppStore((state) => state.activeWorkspace)
  const { data: bots } = useQuery(listBotsQueryOptions(activeWorkspace?.id ?? ''))
  const currentLocation = useLocation()

  const showDeployToast = useCallback(
    (workspaceId: string, botId: string) => {
      const useBotOnboardingStore = getUseBotOnboardingStore({ botId, workspaceId })
      const store = useBotOnboardingStore.getState()

      if (store.deploymentProgress && (store.isDeploying || store.deploymentProgress.currentStep === 'completed')) {
        showStickyToast(
          <BotDeployToast workspaceId={workspaceId} botId={botId} />,
          isMobile ? 'bottom-center' : 'bottom-right',
          {
            id: `deployment-progress-${botId}`,
          }
        )
      }
    },
    [isMobile]
  )

  useEffect(() => {
    if (!activeWorkspace || !bots) {
      return
    }

    bots.forEach((bot) => {
      const useBotOnboardingStore = getUseBotOnboardingStore({ botId: bot.id, workspaceId: activeWorkspace.id })
      const store = useBotOnboardingStore.getState()
      const isOnOnboardingPage = currentLocation.pathname === `/workspaces/${activeWorkspace.id}/bots/${bot.id}/new-bot`

      if (
        store.deploymentProgress &&
        (store.isDeploying || store.deploymentProgress.currentStep === 'completed') &&
        !isOnOnboardingPage
      ) {
        showDeployToast(activeWorkspace.id, bot.id)
      }
    })
  }, [activeWorkspace, bots, showDeployToast, currentLocation])

  return <DeploymentToastContext.Provider value={{ showDeployToast }}>{children}</DeploymentToastContext.Provider>
}
